import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Dialog, Transition, Menu } from "@headlessui/react";
import { Fragment } from "react";
import {
  X,
  Search,
  FileText,
  Calendar,
  User,
  FolderOpen,
  Database,
  Loader,
  AlertCircle,
  Eye,
  ArrowLeft,
  ExternalLink,
  Download,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight, ChevronDown,
  MoveUp,
  MoveDown,
  ArrowDown,
  Trash2,
  MessageSquare,
  Zap,
  Clock,
  UploadCloud,
  AlertTriangle,
  Shield,
  ShieldAlert,
  ShieldCheck
} from "lucide-react";
import { toast } from "sonner";
import { 
  getAllFilesForAdmin,
  getUserAccessibleFiles, 
  listKnowledgeBases,
  generateAdminFileDownloadUrl, 
  generateUserFileDownloadUrl,
  deleteAdminFile,
  deleteUserFile,
  generateKnowledgeBasePresignedUploadUrl,
  uploadFileToS3,
  checkFileDuplicate,
  uploadDirectKnowledgeBaseFile,
  scanFileOnly
} from "../../service/apiService";
import { LockFileType } from "../../types";
import LockButton from "../Lock/LockButton";
import LockBadge from "../Lock/LockBadge";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import * as XLSX from "xlsx";
import { AdminFileManagementResponse, UserFileManagementResponse, ScanIssue } from "../../types";
import SecurityReportModal from "./SecurityReportModal";
import BulkSecurityReportModal, { ScannedFileResult } from "./BulkSecurityReportModal";
import ScanConfirmationModal from "./ScanConfirmationModal";
import SecurityScannerAnimation from "./SecurityScannerAnimation";
import GoogleDriveIcon from '../../icons/googleDrive';
import DropboxIcon from '../../icons/dropbox';
// import GoogleDriveIcon from "../../icons/googleDrive";
import GoogleDriveFilePicker from "../GoogleDrive/GoogleDriveFilePicker";
import GoogleDriveAuth from "../GoogleDrive/GoogleDriveAuth";
import DropboxAuth from "../Dropbox/DropboxAuth";
import DropboxFilePicker from "../Dropbox/DropboxFilePicker";
import { scanGoogleDriveFileOnly, scanDropboxFileOnly, uploadDirectGoogleDriveFile, uploadDirectDropboxFile, checkGoogleDriveConnection, checkDropboxConnection, checkDropboxConfigured, authenticateGoogleDrive } from "../../service/apiService";
import type { GoogleDriveFile } from "../../service/apiService";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FileManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
  userDetails?: any;
  onFileSelect?: (file: FileRow) => void;
  onChatWithFile?: (file: FileRow) => void;
  isFileLoading?: string | null;
}

interface FileRow {
  file_id: string;
  file_name: string;
  original_file_name?: string;
  file_format: string;
  uploaded_at: string;
  uploaded_by_name: string;
  // Needed to decide whether the current admin may lock this file: they may
  // only lock what they uploaded.
  uploaded_by_id?: string;
  storage_location: 'session' | 'brainbank' | 'direct';
  session_id?: string;
  status?: string;
  knowledge_base_id?: string;
  knowledge_base_name?: string;
  department?: string | null;
  processing_mode?: string;
  scan_status?: "clean" | "flagged" | "blocked" | "not_scanned" | "pending" | "failed";
  scan_issues?: ScanIssue[];
  scan_findings?: { type: string; count: number }[];
  last_scan_date?: string | null;
  scan_count?: number;
  // Admin lock. Only ever set on rows the viewer locked themselves -- the API
  // hides everyone else's locked files from this list entirely.
  locked_by?: string | null;
  locked_at?: string | null;
}

interface SessionFile {
  file_id: string;
  file_name: string;
  original_file_name?: string;
  session_id: string;
  session_title: string;
  storage_location: 'session' | 'brainbank' | 'direct';
  uploaded_at: string;
  uploaded_by_id: string;
  uploaded_by_name: string;
  status?: string;
  scan_status?: "clean" | "flagged" | "blocked" | "not_scanned" | "pending" | "failed";
  scan_issues?: ScanIssue[];
  scan_findings?: { type: string; count: number }[];
  last_scan_date?: string | null;
  scan_count?: number;
}

interface BrainbankFile {
  file_id: string;
  file_name: string;
  original_file_name?: string;
  knowledge_base_id: string;
  knowledge_base_name: string;
  storage_location: 'session' | 'brainbank' | 'direct';
  uploaded_at: string;
  uploaded_by_id: string;
  uploaded_by_name: string;
  department?: string | null;
  status?: string;
  processing_mode?: string;
  scan_status?: "clean" | "flagged" | "blocked" | "not_scanned" | "pending" | "failed";
  scan_issues?: ScanIssue[];
  scan_findings?: { type: string; count: number }[];
  last_scan_date?: string | null;
  scan_count?: number;
}

const TableSkeleton = () => (
  <div className="animate-pulse">
    <table className="min-w-full">
      <thead className="bg-white border-b">
        <tr>
          {['NAME', 'TYPE', 'FORMAT', 'BRAINBANK NAME', 'DEPARTMENT', 'PROCESSING MODE', 'UPLOADED', 'UPLOADER', 'ACTIONS'].map((h, i) => (
            <th key={i} className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {[...Array(10)].map((_, i) => (
          <tr key={i} className="bg-white">
            <td className="py-3 px-4"><div className="h-4 bg-gray-200 rounded w-3/4"></div></td>
            <td className="py-3 px-4"><div className="h-5 bg-gray-200 rounded-full w-20"></div></td>
            <td className="py-3 px-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
            <td className="py-3 px-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
            <td className="py-3 px-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
            <td className="py-3 px-4"><div className="h-5 bg-gray-200 rounded-full w-20"></div></td>
            <td className="py-3 px-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
            <td className="py-3 px-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
            <td className="py-3 px-4"><div className="flex space-x-2 justify-center"><div className="h-6 w-6 bg-gray-200 rounded-md"></div><div className="h-6 w-6 bg-gray-200 rounded-md"></div><div className="h-6 w-6 bg-gray-200 rounded-md"></div></div></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);




const UploadSourceModal = ({ isOpen, onClose, onLocalClick, onGoogleDriveClick, onDropboxClick, isDropboxConfigured }: { isOpen: boolean, onClose: () => void, onLocalClick: () => void, onGoogleDriveClick: () => void, onDropboxClick: () => void, isDropboxConfigured: boolean }) => {
  if (!isOpen) return null;

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-[90]" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-white px-6 pb-6 pt-5 sm:p-8 sm:pb-6">
                  <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <X size={24} aria-hidden="true" />
                    </button>
                  </div>
                  
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900 mb-6 flex items-center justify-center">
                        <UploadCloud size={24} className="mr-3 text-violet-600" />
                        Select Upload Source
                      </Dialog.Title>
                      
                      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <button
                          onClick={() => {
                            onLocalClick();
                            onClose();
                          }}
                          className="group relative flex flex-col items-center justify-center p-8 border-2 border-gray-200 rounded-xl hover:border-violet-500 hover:bg-violet-50 transition-all duration-200"
                        >
                          <div className="h-16 w-16 rounded-full bg-violet-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                            <FolderOpen size={32} className="text-violet-600" />
                          </div>
                          <span className="text-base font-medium text-gray-900">Local Upload</span>
                          <span className="text-xs text-gray-500 mt-2 text-center">Upload files from your computer</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            onGoogleDriveClick();
                            onClose();
                          }}
                          className="group relative flex flex-col items-center justify-center p-8 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200"
                        >
                          <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                            <GoogleDriveIcon size={32} className="text-blue-600" />
                          </div>
                          <span className="text-base font-medium text-gray-900">Google Drive</span>
                          <span className="text-xs text-gray-500 mt-2 text-center">Import directly from Drive</span>
                        </button>
                        <button
                          onClick={() => {
                            if (!isDropboxConfigured) return;
                            onDropboxClick();
                            onClose();
                          }}
                          disabled={!isDropboxConfigured}
                          title={!isDropboxConfigured ? "Dropbox integration is not configured. Please contact your administrator." : "Import directly from Dropbox"}
                          className={`group relative flex flex-col items-center justify-center p-8 border-2 border-gray-200 rounded-xl transition-all duration-200 ${
                            isDropboxConfigured 
                              ? 'hover:border-blue-600 hover:bg-blue-50 cursor-pointer' 
                              : 'opacity-50 cursor-not-allowed bg-gray-50'
                          }`}
                        >
                          <div className={`h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mb-4 transition-transform duration-200 ${isDropboxConfigured ? 'group-hover:scale-110' : ''}`}>
                            <DropboxIcon size={32} className="text-blue-600" />
                          </div>
                          <span className="text-base font-medium text-gray-900">Dropbox</span>
                          <span className="text-xs text-gray-500 mt-2 text-center">
                            {isDropboxConfigured ? "Import directly from Dropbox" : "Not configured"}
                          </span>
                        </button>

                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

const UploadWorkflowButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <div className="relative group inline-block mr-2 z-20">
      <button
        onClick={onClick}
        className="flex items-center px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors text-sm focus:outline-none"
      >
        <UploadCloud size={16} className="mr-2" />
        Upload Files
      </button>

      {/* Tooltip Wrapper */}
      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden md:group-hover:flex flex-col items-center z-[100] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-max max-w-xs">
        {/* Tooltip Arrow */}
        <div className="w-3 h-3 bg-white transform rotate-45 border-t border-l border-gray-200 -mb-1.5 z-10"></div>
        
        {/* Tooltip Content */}
        <div className="bg-white border border-gray-200 shadow-xl rounded-lg p-3 flex items-center space-x-3 text-gray-800">
          
          {/* Animation Column */}
          <div className="flex flex-col items-center justify-center space-y-1 w-6 h-[86px] overflow-hidden upload-workflow-anim">
            <style>{`
              .upload-workflow-anim > .static-shield {
                display: none;
              }
              @media (prefers-reduced-motion: reduce) {
                .upload-workflow-anim > .seq-anim-container {
                  display: none;
                }
                .upload-workflow-anim > .static-shield {
                  display: block !important;
                }
              }
              .seq-anim-container {
                position: relative;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
              }
              .seq-item {
                position: absolute;
                opacity: 0;
                animation: seqAnim 2.5s infinite;
              }
              @keyframes seqAnim {
                0% { opacity: 0; transform: translateY(5px); }
                10%, 80% { opacity: 1; transform: translateY(0); }
                90%, 100% { opacity: 0; transform: translateY(-5px); }
              }
              .seq-1 { animation-delay: 0ms; top: 0; }
              .seq-2 { animation-delay: 150ms; top: 18px; }
              .seq-3 { animation-delay: 300ms; top: 34px; }
              .seq-4 { animation-delay: 450ms; top: 52px; }
              .seq-5 { animation-delay: 600ms; top: 68px; }
            `}</style>
            
            <ShieldCheck size={18} className="static-shield text-green-500" />
            
            <div className="seq-anim-container">
               <FileText size={14} className="seq-item seq-1 text-gray-500" />
               <ArrowDown size={12} className="seq-item seq-2 text-gray-400" />
               <ShieldCheck size={14} className="seq-item seq-3 text-green-500" />
               <ArrowDown size={12} className="seq-item seq-4 text-gray-400" />
               <UploadCloud size={14} className="seq-item seq-5 text-violet-500" />
            </div>
          </div>

          <div className="text-xs text-left max-w-[160px] leading-snug">
            Files are automatically security scanned before upload.
          </div>
        </div>
      </div>
    </div>
  );
};

const FileManagementModal: React.FC<FileManagementModalProps> = ({
  isOpen,
  onClose,
  isAdmin = false,
  userDetails,
  onFileSelect,
  isFileLoading,
  onChatWithFile,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [fileData, setFileData] = useState<AdminFileManagementResponse | UserFileManagementResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState<any[]>([]);
  const [isDeletingFileId, setIsDeletingFileId] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{
    id: string;
    name: string;
    type: 'session' | 'brainbank';
  } | null>(null);

  const [uploadingFiles, setUploadingFiles] = useState<{
    id: string;
    name: string;
    progress: number;
    status: 'checking' | 'uploading' | 'error' | 'success';
    error?: string;
    format: string;
  }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Security Report modal state
  const [showSecurityReport, setShowSecurityReport] = useState(false);

  // Scan confirmation modal state
  const [showScanConfirmation, setShowScanConfirmation] = useState(false);
  const [isScanningFiles, setIsScanningFiles] = useState(false);
  const [bulkScanResults, setBulkScanResults] = useState<ScannedFileResult[]>([]);
  const [showBulkSecurityReport, setShowBulkSecurityReport] = useState(false);
  const [pendingScanFiles, setPendingScanFiles] = useState<File[]>([]);
  const [securityReportFile, setSecurityReportFile] = useState<FileRow | null>(null);

  const [duplicateFiles, setDuplicateFiles] = useState<{
    file: File;
    name: string;
    format: string;
    hash: string;
    existingFileName: string;
    isGoogleDrive?: boolean;
    googleDriveId?: string;
    isDropbox?: boolean;
    dropboxFileId?: string;
    scanId?: string;
    scanEnabled?: boolean;
  }[]>([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  // Google Drive Integration
  const [isGoogleDrivePickerOpen, setIsGoogleDrivePickerOpen] = useState(false);
  const [showUploadSourceModal, setShowUploadSourceModal] = useState(false);
  const [isDropboxConnected, setIsDropboxConnected] = useState(false);
  const [isDropboxConfigured, setIsDropboxConfigured] = useState(false);
  const [isDropboxAuthOpen, setIsDropboxAuthOpen] = useState(false);
  const [isDropboxPickerOpen, setIsDropboxPickerOpen] = useState(false);
  const [isGoogleDriveAuthOpen, setIsGoogleDriveAuthOpen] = useState(false);
  const [isGoogleDriveConnected, setIsGoogleDriveConnected] = useState(false);


  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pendingSearch, setPendingSearch] = useState("");
  
  // New Filters
  const [scanStatusFilter, setScanStatusFilter] = useState<string>("");
  const [findingTypeFilter, setFindingTypeFilter] = useState<string>("");
  
  // View state
  const [view, setView] = useState<'list' | 'preview'>('list');
  
  // File preview states
  const [activeFile, setActiveFile] = useState<{
    id: string;
    name: string;
    format: string;
    type: 'session' | 'brainbank' | 'direct';
    url: string;
  } | null>(null);
  const [loadingFileId, setLoadingFileId] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  
  // Content states for different file types
  const [excelData, setExcelData] = useState<any[] | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  
  // Text search functionality
  const [fileSearchTerm, setFileSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const contentRef = useRef<HTMLDivElement>(null);
  
  // PDF specific states
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);

  // Fetch knowledge bases
  useEffect(() => {
    const fetchKnowledgeBases = async () => {
      try {
        const kbData = await listKnowledgeBases();
        setKnowledgeBases(kbData || []);
      } catch (error) {
        console.error("Error fetching knowledge bases:", error);
      }
    };

    const fetchConfigurations = async () => {
      try {
        const dropboxConfigured = await checkDropboxConfigured();
        setIsDropboxConfigured(dropboxConfigured);
        
        const googleDriveConnected = await checkGoogleDriveConnection();
        setIsGoogleDriveConnected(googleDriveConnected);
        
        const dropboxConnected = await checkDropboxConnection();
        setIsDropboxConnected(dropboxConnected);
      } catch (error) {
        console.error("Error fetching configurations:", error);
      }
    };

    if (isOpen) {
      fetchKnowledgeBases();
      fetchConfigurations();
    }
  }, [isOpen]);
  
  const cancelTokenSourceRef = useRef<import("axios").CancelTokenSource | null>(null);

  const fetchFiles = async (page: number = currentPage, size: number = pageSize, searchQuery?: string) => {
    if (!isOpen) return;

    // Abort previous request
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel("canceled");
    }
    const source = axios.CancelToken.source();
    cancelTokenSourceRef.current = source;

    // Set appropriate loading state
    if (searchQuery && searchQuery.trim()) {
      setIsSearching(true);
    } else {
      setLoading(true);
    }

    try {
      const data = isAdmin 
        ? await getAllFilesForAdmin(page, size, searchQuery, scanStatusFilter, findingTypeFilter, source.token) 
        : await getUserAccessibleFiles(page, size, searchQuery, scanStatusFilter, findingTypeFilter, source.token);
      
      setFileData(data);
      
      // Update pagination state
      if (data.pagination) {
        setCurrentPage(data.pagination.current_page);
        setTotalPages(data.pagination.total_pages);
        setTotalItems(data.pagination.total_items);
      }
    } catch (error: any) {
      if (axios.isCancel(error) || error?.message === 'canceled') {
        // Request was aborted, ignore silently
        return;
      }
      console.error("Error fetching files:", error);
      toast.error("Failed to load files");
    } finally {
      if (cancelTokenSourceRef.current === source) {
        setLoading(false);
        setIsSearching(false);
      }
    }
  };

  // Debounced search logic
  useEffect(() => {
    const timer = setTimeout(() => {
      // Avoid fetching if pendingSearch hasn't effectively changed
      if (pendingSearch !== searchTerm && isOpen) {
        const trimmedQuery = pendingSearch.trim();
        setSearchTerm(trimmedQuery);
        
        if (!trimmedQuery) {
          fetchFiles(1, pageSize); 
        } else {
          fetchFiles(1, pageSize, trimmedQuery);
        }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [pendingSearch, searchTerm, isOpen, pageSize]);

  // Handle search button click
  const handleSearchClick = async () => {
    const trimmedQuery = pendingSearch.trim();
    setSearchTerm(trimmedQuery);
    
    await fetchFiles(1, pageSize, trimmedQuery || undefined);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingSearch(e.target.value);
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  // Handle clear search
  const handleClearSearch = async () => {
    setPendingSearch("");
    setSearchTerm("");
    setScanStatusFilter("");
    setFindingTypeFilter("");
    setFileData(null); 
    // fetchFiles relies on state so we pass explicit empty values to ensure it clears
    const source = axios.CancelToken.source();
    cancelTokenSourceRef.current = source;
    setLoading(true);
    try {
      const data = isAdmin 
        ? await getAllFilesForAdmin(1, pageSize, undefined, "", "", source.token) 
        : await getUserAccessibleFiles(1, pageSize, undefined, "", "", source.token);
      setFileData(data);
      if (data.pagination) {
        setCurrentPage(data.pagination.current_page);
        setTotalPages(data.pagination.total_pages);
        setTotalItems(data.pagination.total_items);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Compute SHA-256 hash of a file using Web Crypto API
  const computeFileHash = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Upload a single file (called after duplicate check passes or user clicks "Upload Anyway")
  const proceedWithUpload = async (file: File, uploadId: string, fileHash: string, scanEnabled?: boolean, scanId?: string) => {
    const parts = file.name.split('.');
    const format = parts.length > 1 ? parts.pop()?.toUpperCase() || 'Unknown' : 'Unknown';

    setUploadingFiles(prev => [
      { id: uploadId, name: file.name, format, progress: 0, status: 'uploading' },
      ...prev.filter(f => f.id !== uploadId)
    ]);

    try {
      // Use direct upload to allow backend guardrails scanning
      const response = await uploadDirectKnowledgeBaseFile(
        file,
        "unassigned",
        "unassigned",
        "standard",
        fileHash,
        scanEnabled,
        scanId
      );

      if (!response.success || !response.file_id) {
        throw new Error("Failed to upload file");
      }

      const scanResult = response.scan_result;

      if (scanResult && scanResult.status === 'blocked') {
        const issues = scanResult.issues || [];
        const uniqueLabels = Array.from(new Set(issues.map((i: any) => i.label || i.type)));
        toast.error(`Security Alert: ${file.name}`, {
          description: `Detected: ${uniqueLabels.join(', ')}. File saved with security findings.`
        });
      } else if (scanResult && scanResult.status === 'flagged') {
        toast.warning(
          `File "${file.name}" uploaded but flagged for sensitive content.`,
          { description: `${scanResult.issues?.length || 0} policy violations detected.` }
        );
      }

      setUploadingFiles(prev => prev.map(f => f.id === uploadId ? { ...f, status: 'success', progress: 100 } : f));

      const newFile = {
        file_id: response.file_id,
        file_name: file.name,
        knowledge_base_id: "unassigned",
        knowledge_base_name: null,
        storage_location: 'direct',
        uploaded_at: new Date().toISOString(),
        uploaded_by_id: userDetails?.id || "Unknown",
        uploaded_by_name: userDetails?.name || "You",
        status: scanResult?.status === 'flagged' ? 'flagged' : 'processed',
        processing_mode: 'standard',
        department: null,
        scan_status: scanResult?.status || 'clean',
        scan_issues: scanResult?.issues || []
      };

      setFileData(prev => {
        if (!prev) return prev;
        const prevData = { ...prev } as any;
        if (!prevData.brainbank_files) prevData.brainbank_files = [];
        return {
          ...prevData,
          brainbank_files: [newFile, ...prevData.brainbank_files]
        };
      });

      setTimeout(() => {
        setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
      }, 2000);

    } catch (error: any) {
      console.error("Upload failed for", file.name, error);

      let errorMessage = error.response?.data?.detail?.message || error.message || "Upload failed";

      setUploadingFiles(prev => prev.map(f => f.id === uploadId ? { ...f, status: 'error', error: errorMessage } : f));

      toast.error(`Upload Failed: ${file.name}`, {
        description: errorMessage
      });
    }
  };

  
  const handleGoogleDriveUploadClick = () => {
    if (isGoogleDriveConnected) {
      setIsGoogleDrivePickerOpen(true);
    } else {
      setIsGoogleDriveAuthOpen(true);
    }
  };

  const handleGoogleDriveAuthSuccess = async (code: string) => {
    try {
      const result = await authenticateGoogleDrive(code);
      if (result.success) {
        setIsGoogleDriveConnected(true);
        setIsGoogleDriveAuthOpen(false);
        setIsGoogleDrivePickerOpen(true);
      } else {
        toast.error("Google Drive connection failed");
      }
    } catch (e) {
      toast.error("Google Drive connection error");
    }
  };

  
  const handleDropboxUploadClick = () => {
    if (isDropboxConnected) {
      setIsDropboxPickerOpen(true);
    } else {
      setIsDropboxAuthOpen(true);
    }
  };

  const handleDropboxAuthSuccess = () => {
    setIsDropboxConnected(true);
    setIsDropboxAuthOpen(false);
    setIsDropboxPickerOpen(true);
  };

  const handleDropboxFileSelect = async (dFile: any) => {
    setIsScanningFiles(true);
    
    const dummyFile = new File([], dFile.name, { type: dFile.mimeType });
    setPendingScanFiles([dummyFile]);

    try {
      const response = await scanDropboxFileOnly(dFile.id, true);
      const result: ScannedFileResult = {
        fileName: response.filename,
        scanStatus: response.scan_result.status,
        scanIssues: response.scan_result.issues || [],
        scanId: response.scan_id,
        isDropbox: true,
        dropboxFileId: dFile.id,
        fileHash: response.file_hash,
        fileSize: response.file_size
      };
      setBulkScanResults([result]);
      setIsScanningFiles(false);
      setShowBulkSecurityReport(true);
    } catch (error) {
      console.error("Error scanning Dropbox file:", error);
      setIsScanningFiles(false);
      toast.error("Failed to scan Dropbox file", {
        description: "Please try again or select a different file."
      });
      setPendingScanFiles([]);
    }
  };

  const handleGoogleDriveFileSelect = async (gFile: GoogleDriveFile) => {
    setIsScanningFiles(true);
    
    const dummyFile = new File([], gFile.name, { type: gFile.mimeType });
    setPendingScanFiles([dummyFile]);

    try {
      const response = await scanGoogleDriveFileOnly(gFile.id, true);
      const result: ScannedFileResult = {
        fileName: response.filename,
        scanStatus: response.scan_result.status,
        scanIssues: response.scan_result.issues || [],
        scanId: response.scan_id,
        isGoogleDrive: true,
        googleDriveId: gFile.id,
        fileHash: response.file_hash,
        fileSize: response.file_size
      };
      setBulkScanResults([result]);
      setIsScanningFiles(false);
      setShowBulkSecurityReport(true);
    } catch (error) {
      console.error("Google Drive scan failed", error);
      setIsScanningFiles(false);
      toast.error("Security Scan Failed", { description: "Failed to scan Google Drive file." });
      setPendingScanFiles([]);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []) as File[];
    if (!files.length) return;

    if (fileInputRef.current) fileInputRef.current.value = "";

    setPendingScanFiles(files);
    setIsScanningFiles(true);
    
    try {
      const results: ScannedFileResult[] = [];
      for (const file of files) {
        const response = await scanFileOnly(file, true);
        results.push({
          fileName: file.name,
          scanStatus: response.scan_result.status,
          scanIssues: response.scan_result.issues || [],
          scanId: response.scan_id
        });
      }
      setBulkScanResults(results);
      setIsScanningFiles(false);
      setShowBulkSecurityReport(true);
    } catch (error) {
      console.error("Scanning failed", error);
      setIsScanningFiles(false);
      toast.error("Security Scan Failed", {
        description: "Failed to scan files. Please try again."
      });
      setPendingScanFiles([]);
    }
  };

  // Called after user makes scan choice in the confirmation modal
  const executeUpload = async (files: File[], scanEnabled?: boolean, scanResults?: ScannedFileResult[]) => {
    const newUploads = files.map(file => {
      const parts = file.name.split('.');
      const format = parts.length > 1 ? parts.pop()?.toUpperCase() || 'Unknown' : 'Unknown';
      return {
        id: Math.random().toString(36).substring(7),
        file,
        name: file.name,
        format,
        progress: 0,
        status: 'checking' as const
      };
    });

    // Show checking status in the table
    setUploadingFiles(prev => [...newUploads.map(u => ({ id: u.id, name: u.name, format: u.format, progress: u.progress, status: u.status })), ...prev]);

    const detectedDuplicates: typeof duplicateFiles = [];

    for (const upload of newUploads) {
      try {
        // Step 1: Compute SHA-256 hash
        const matchingResultForHash = scanResults?.find(r => r.fileName === upload.name);
        const fileHash = ((matchingResultForHash?.isGoogleDrive || matchingResultForHash?.isDropbox) && matchingResultForHash?.fileHash) ? matchingResultForHash.fileHash : await computeFileHash(upload.file);

        // Step 2: Check for duplicate
        const dupResult = await checkFileDuplicate(upload.name, upload.file.size, fileHash);

        if (dupResult.duplicate) {
          // Mark as duplicate — remove from uploading list and collect for modal
          setUploadingFiles(prev => prev.filter(f => f.id !== upload.id));
          const matchingResult = scanResults?.find(r => r.fileName === upload.name);
          detectedDuplicates.push({
            file: upload.file,
            name: upload.name,
            format: upload.format,
            hash: fileHash,
            existingFileName: dupResult.existing_file_name || upload.name,
            isGoogleDrive: matchingResult?.isGoogleDrive,
            googleDriveId: matchingResult?.googleDriveId,
            isDropbox: matchingResult?.isDropbox,
            dropboxFileId: matchingResult?.dropboxFileId,
            scanId: matchingResult?.scanId,
            scanEnabled: scanEnabled
          });
        } else {
          // No duplicate — proceed with upload, passing the scan choice
          const matchingResult = scanResults?.find(r => r.fileName === upload.name);
          if (matchingResult?.isGoogleDrive && matchingResult.googleDriveId) {
             try {
               await uploadDirectGoogleDriveFile(
                 matchingResult.googleDriveId,
                 "unassigned",
                 "unassigned",
                 "standard",
                 fileHash,
                 scanEnabled,
                 matchingResult.scanId
               );
               setUploadingFiles(prev => prev.map(f => f.id === upload.id ? { ...f, status: 'success', progress: 100 } : f));
             } catch (err: any) {
               setUploadingFiles(prev => prev.map(f => f.id === upload.id ? { ...f, status: 'error', error: err.message || "Failed to upload" } : f));
             }
          } else if (matchingResult?.isDropbox && matchingResult.dropboxFileId) {
             try {
               await uploadDirectDropboxFile(
                 matchingResult.dropboxFileId,
                 "unassigned",
                 "unassigned",
                 "standard",
                 fileHash,
                 scanEnabled,
                 matchingResult.scanId
               );
               setUploadingFiles(prev => prev.map(f => f.id === upload.id ? { ...f, status: 'success', progress: 100 } : f));
             } catch (err: any) {
               setUploadingFiles(prev => prev.map(f => f.id === upload.id ? { ...f, status: 'error', error: err.message || "Failed to upload" } : f));
             }
          } else {
             await proceedWithUpload(upload.file, upload.id, fileHash, scanEnabled, matchingResult?.scanId);
          }
        }
      } catch (error: any) {
        console.error("Upload check failed for", upload.name, error);
        setUploadingFiles(prev => prev.map(f => f.id === upload.id ? { ...f, status: 'error', error: error.message || "Check failed" } : f));
      }
    }

    // Show duplicate modal if any duplicates were found
    if (detectedDuplicates.length > 0) {
      setDuplicateFiles(detectedDuplicates);
      setShowDuplicateModal(true);
    }
  };

  // Handle user actions on duplicate files
  const handleSkipDuplicate = (index: number) => {
    setDuplicateFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) {
        // Delay closing so the mouse-up doesn't fall through to the Dialog backdrop
        setTimeout(() => setShowDuplicateModal(false), 0);
      }
      return updated;
    });
  };

  const handleUploadAnyway = async (index: number) => {
    const dup = duplicateFiles[index];
    setDuplicateFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length === 0) {
        setTimeout(() => setShowDuplicateModal(false), 0);
      }
      return updated;
    });
    
    // Proceed with upload using existing hash
    const uploadId = Math.random().toString(36).substring(7);
    
    // Show uploading state
    setUploadingFiles(prev => [
      { id: uploadId, name: dup.file.name, format: dup.format, progress: 0, status: 'uploading' },
      ...prev.filter(f => f.id !== uploadId)
    ]);
    
    if (dup.isGoogleDrive && dup.googleDriveId) {
      try {
        await uploadDirectGoogleDriveFile(
          dup.googleDriveId,
          "unassigned",
          "unassigned",
          "standard",
          dup.hash,
          dup.scanEnabled,
          dup.scanId
        );
        setUploadingFiles(prev => prev.map(f => f.id === uploadId ? { ...f, status: 'success', progress: 100 } : f));
      } catch (err: any) {
        setUploadingFiles(prev => prev.map(f => f.id === uploadId ? { ...f, status: 'error', error: err.message || "Failed to upload Google Drive file" } : f));
      }
    } else if (dup.isDropbox && dup.dropboxFileId) {
      try {
        await uploadDirectDropboxFile(
          dup.dropboxFileId,
          "unassigned",
          "unassigned",
          "standard",
          dup.hash,
          dup.scanEnabled,
          dup.scanId
        );
        setUploadingFiles(prev => prev.map(f => f.id === uploadId ? { ...f, status: 'success', progress: 100 } : f));
      } catch (err: any) {
        setUploadingFiles(prev => prev.map(f => f.id === uploadId ? { ...f, status: 'error', error: err.message || "Failed to upload Dropbox file" } : f));
      }
    } else {
      await proceedWithUpload(dup.file, uploadId, dup.hash, dup.scanEnabled, dup.scanId);
    }
  };

  const handleSkipAllDuplicates = () => {
    setDuplicateFiles([]);
    setTimeout(() => setShowDuplicateModal(false), 0);
  };

// Fetch files when modal opens or filters change
useEffect(() => {
  if (isOpen) {
    fetchFiles(1, pageSize); 
  }
}, [isOpen, isAdmin, scanStatusFilter, findingTypeFilter]);

const handlePageChange = async (newPage: number) => {
  if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
    const searchQuery = searchTerm.trim() ? searchTerm.trim() : undefined;
    await fetchFiles(newPage, pageSize, searchQuery);
  }
};

const handlePageSizeChange = async (newPageSize: number) => {
  setPageSize(newPageSize);
  const searchQuery = searchTerm.trim() ? searchTerm.trim() : undefined;
  await fetchFiles(1, newPageSize, searchQuery);
};

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setView('list');
      setActiveFile(null);
      setFileError(null);
      setSearchTerm("");
      setPendingSearch("");
      setNumPages(0);
      setPageNumber(1);
      setScale(1.0);
      setExcelData(null);
      setTextContent(null);
      setFileSearchTerm("");
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setCurrentPage(1);
      setPageSize(20);
      setTotalPages(1);
      setTotalItems(0);
      setIsSearching(false);
      setScanStatusFilter("");
      setFindingTypeFilter("");
    }
  }, [isOpen]);

  // Search in text file content
  useEffect(() => {
    if (!textContent || !fileSearchTerm.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setPendingSearch("");
      return;
    }
    
    const results: number[] = [];
    const text = textContent.toLowerCase();
    const searchTermLower = fileSearchTerm.toLowerCase();
    let index = 0;
    
    while (index < text.length) {
      index = text.indexOf(searchTermLower, index);
      if (index === -1) break;
      results.push(index);
      index += searchTermLower.length;
    }
    
    setSearchResults(results);
    setCurrentSearchIndex(results.length > 0 ? 0 : -1);
  }, [textContent, fileSearchTerm]);

  // Scroll to search result
  useEffect(() => {
    if (currentSearchIndex >= 0 && searchResults.length > 0 && contentRef.current) {
      const currentResult = searchResults[currentSearchIndex];
      const textUptoCurrent = textContent?.substring(0, currentResult) || '';
      
      const lines = textUptoCurrent.split('\n');
      const lineIndex = lines.length - 1;
      
      const lineElement = contentRef.current.querySelector(`[data-line-number="${lineIndex + 1}"]`);
      if (lineElement) {
        lineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentSearchIndex, searchResults, textContent]);

  const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop()?.toUpperCase() || 'Unknown' : 'Unknown';
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  const handleSearchNext = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prev) => (prev + 1) % searchResults.length);
  };
  
  const handleSearchPrevious = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
  };

  // Modified function to determine if a file can be previewed inline
  const isPreviewableInline = (format: string) => {
    format = format.toLowerCase();
    return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt', 'csv', 'xlsx', 'xls', 'docx', 'doc', 'json', 'xml'].includes(format);
  };

  // Handle preview click - fetch URL and show inline preview
  const handlePreviewClick = async (file: FileRow) => {
    if (loadingFileId === file.file_id) return;
    
    setLoadingFileId(file.file_id);
    setFileError(null);
    setExcelData(null);
    setTextContent(null);
    setFileSearchTerm("");
    setSearchResults([]);
    setCurrentSearchIndex(-1);
    
    try {
      let response;
      
      const apiFileType = file.storage_location === 'direct' ? 'brainbank' : file.storage_location;
      if (isAdmin) {
        response = await generateAdminFileDownloadUrl(file.file_id, apiFileType);
      } else {
        response = await generateUserFileDownloadUrl(file.file_id, apiFileType);
      }

      if (response && response.presigned_url) {
        const format = getFileExtension(file.file_name).toLowerCase();
        const fileUrl = response.presigned_url;
        
        // For Excel files, fetch and parse the data
        if (['xlsx', 'xls'].includes(format.toLowerCase())) {
          try {
            const excelResponse = await fetch(fileUrl);
            if (!excelResponse.ok) throw new Error(`Failed to fetch Excel file: ${excelResponse.status}`);

            const arrayBuffer = await excelResponse.arrayBuffer();
            const data = new Uint8Array(arrayBuffer);

            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            setExcelData(jsonData);
          } catch (excelError) {
            console.error("Error parsing Excel file:", excelError);
            // Don't set error here, we'll use Microsoft Office Viewer as fallback
          }
        }
        
        // For text files, fetch the text content
        if (['txt', 'csv', 'json', 'xml', 'log', 'md'].includes(format.toLowerCase())) {
          try {
            const textResponse = await fetch(fileUrl);
            if (!textResponse.ok) throw new Error(`Failed to fetch text file: ${textResponse.status}`);
            
            const text = await textResponse.text();
            setTextContent(text);
          } catch (textError) {
            console.error("Error fetching text file:", textError);
            setFileError("Failed to load text file");
            setLoadingFileId(null);
            return;
          }
        }
        
        setActiveFile({
          id: file.file_id,
          name: file.file_name,
          format: format.toLowerCase(),
          type: file.storage_location,
          url: fileUrl
        });
        setView('preview');
      } else {
        throw new Error("Failed to generate presigned URL");
      }
    } catch (err: any) {
      console.error("Error fetching presigned URL:", err);
      
      let errorMessage = "Failed to load file preview";
      
      if (err.response?.status === 403) {
        errorMessage = "You don't have permission to view this file";
      } else if (err.response?.status === 404) {
        errorMessage = "File not found";
      } else if (err.response?.status === 401) {
        errorMessage = "Your session has expired. Please log in again.";
      }
      
      setFileError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoadingFileId(null);
    }
  };

  const handleBackToList = () => {
    setView('list');
    setActiveFile(null);
    setFileError(null);
    setNumPages(0);
    setPageNumber(1);
    setScale(1.0);
    setExcelData(null);
    setTextContent(null);
    setFileSearchTerm("");
    setSearchResults([]);
    setCurrentSearchIndex(-1);
  };

  const handleDownload = () => {
    if (!activeFile?.url) return;
    
    window.open(activeFile.url, '_blank');
  };

  /**
 * Which table a row lives in, for the lock endpoints.
 *
 * Same mapping the delete path uses: 'direct' uploads are stored as BrainBank
 * files, so anything that is not a session file is a brainbank file.
 */
const lockFileType = (file: FileRow): LockFileType =>
  file.storage_location === 'session' ? 'session' : 'brainbank';

// Add these functions right after handleDownload
const openDeleteConfirmation = (file: FileRow) => {
  setFileToDelete({
    id: file.file_id,
    name: file.file_name,
    type: file.storage_location === 'direct' ? 'brainbank' : file.storage_location
  });
  setShowDeleteConfirmation(true);
};

const handleDeleteFile = async () => {
  if (!fileToDelete) return;
  
  setIsDeletingFileId(fileToDelete.id);
  
  try {
    const response = isAdmin 
      ? await deleteAdminFile(fileToDelete.id, fileToDelete.type)
      : await deleteUserFile(fileToDelete.id, fileToDelete.type);
    
    if (response.success) {
      toast.success(`File "${fileToDelete.name}" deleted successfully`);
      
      await fetchFiles(currentPage, pageSize);
    } else {
      throw new Error(response.message || "Failed to delete file");
    }
  } catch (err: any) {
    console.error("Error deleting file:", err);
    
    let errorMessage = "Failed to delete file";
    
    if (err.response?.status === 403) {
      errorMessage = "You don't have permission to delete this file";
    } else if (err.response?.status === 404) {
      errorMessage = "File not found";
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    toast.error(errorMessage);
  } finally {
    setIsDeletingFileId(null);
    setShowDeleteConfirmation(false);
    setFileToDelete(null);
  }
};

  // PDF handlers
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };
  
  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));
  
  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 3.0));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));

  // Create Microsoft Office Online Viewer URL for Excel files
  const createExcelViewerUrl = () => {
    if (!activeFile?.url) return '';
    const encodedFileUrl = encodeURIComponent(activeFile.url);
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodedFileUrl}`;
  };

  // Transform API data into table rows
  const fileRows: FileRow[] = useMemo(() => {
    if (!fileData) return [];

    const rows: FileRow[] = [];

    // Process session files with proper typing
    if ('session_files' in fileData && fileData.session_files) {
      fileData.session_files.forEach((file: SessionFile) => {
        rows.push({
          file_id: file.file_id,
          file_name: file.file_name || 'Unknown File',
          original_file_name: file.original_file_name,
          file_format: getFileExtension(file.file_name || 'Unknown File'),
          uploaded_at: file.uploaded_at,
          uploaded_by_name: file.uploaded_by_name,
          uploaded_by_id: file.uploaded_by_id,
          storage_location: 'session',
          session_id: file.session_id,
          knowledge_base_name: undefined,
          department: undefined,
          // Carried through so the row can show its lock badge -- without
          // these the fields the API returns would be dropped here.
          locked_by: (file as any).locked_by ?? null,
          locked_at: (file as any).locked_at ?? null,
        });
      });
    }

    // Process brainbank files with proper typing
    if ('brainbank_files' in fileData && fileData.brainbank_files) {
      fileData.brainbank_files.forEach((file: BrainbankFile) => {
        rows.push({
          file_id: file.file_id,
          file_name: file.file_name || 'Unknown File',
          original_file_name: file.original_file_name,
          file_format: getFileExtension(file.file_name || 'Unknown File'),
          uploaded_at: file.uploaded_at,
          uploaded_by_name: file.uploaded_by_name,
          uploaded_by_id: file.uploaded_by_id,
          storage_location: file.storage_location || 'brainbank',
          status: file.status,
          knowledge_base_id: file.knowledge_base_id,
          knowledge_base_name: file.knowledge_base_name,
          department: file.department,
          processing_mode: file.processing_mode,
          scan_status: (file as any).scan_status || "clean",
          scan_issues: (file as any).scan_issues || [],
          scan_findings: (file as any).scan_findings || [],
          last_scan_date: (file as any).last_scan_date || null,
          scan_count: (file as any).scan_count || 0,
          // Carried through so the row can show its lock badge -- without
          // these the fields the API returns would be dropped here.
          locked_by: (file as any).locked_by ?? null,
          locked_at: (file as any).locked_at ?? null,
        });
      });
    }

    return rows;
  }, [fileData]);

  // Use fileRows directly since backend now handles search filtering
const filteredFiles = fileRows;

  const getFileIcon = (format: string) => {
    const baseProps = { size: 16 };
    
    switch (format.toLowerCase()) {
      case 'pdf':
        return <FileText {...baseProps} className="text-red-600" />;
      case 'doc':
      case 'docx':
        return <FileText {...baseProps} className="text-blue-600" />;
      case 'xls':
      case 'xlsx':
        return <FileText {...baseProps} className="text-green-600" />;
      case 'ppt':
      case 'pptx':
        return <FileText {...baseProps} className="text-orange-600" />;
      default:
        return <FileText {...baseProps} className="text-gray-600" />;
    }
  };

  const getFileTypeBadge = (storageLocation: string, status?: string) => {
    if (storageLocation === 'session') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
          Session
        </span>
      );
    } else if (storageLocation === 'direct') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-violet-100 text-violet-800">
          <UploadCloud size={12} className="mr-1" />
          Direct Upload
        </span>
      );
    } else {
      const statusColor = status === 'processed' || status === 'completed' ? 'bg-green-100 text-green-800' : 
                         status === 'flagged' ? 'bg-orange-100 text-orange-800' :
                         status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                         status === 'failed' ? 'bg-red-100 text-red-800' :
                         'bg-gray-100 text-gray-800';
      
      return (
        <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${statusColor}`}>
          <Database size={12} className="mr-1" />
          BrainBank
        </span>
      );
    }
  };
  
  // Function to render text content with search highlighting
  const renderTextContent = () => {
    if (!textContent) return null;
    
    const lines = textContent.split('\n');
    
    return (
      <div ref={contentRef} className="text-sm font-mono p-4">
        <div className="overflow-x-auto">
          {lines.map((line, lineIndex) => {
            if (fileSearchTerm.trim() === '') {
              return (
                <div key={`line-${lineIndex}`} className="leading-6" data-line-number={lineIndex + 1}>
                  {line || ' '}
                </div>
              );
            }
            
            const lineSearchTerm = fileSearchTerm.toLowerCase();
            const lineLower = line.toLowerCase();
            const matches: { index: number, length: number }[] = [];
            let searchIndex = 0;
            
            while (searchIndex < line.length) {
              const foundIndex = lineLower.indexOf(lineSearchTerm, searchIndex);
              if (foundIndex === -1) break;
              matches.push({ index: foundIndex, length: fileSearchTerm.length });
              searchIndex = foundIndex + lineSearchTerm.length;
            }
            
            if (matches.length === 0) {
              return (
                <div key={`line-${lineIndex}`} className="leading-6" data-line-number={lineIndex + 1}>
                  {line || ' '}
                </div>
              );
            }
            
            const parts: JSX.Element[] = [];
            let lastIndex = 0;
            
            const lineStartIndex = textContent.split('\n').slice(0, lineIndex).join('\n').length + (lineIndex > 0 ? 1 : 0);
            
            matches.forEach((match, matchIndex) => {
              const absoluteMatchIndex = lineStartIndex + match.index;
              
              if (match.index > lastIndex) {
                parts.push(
                  <span key={`before-${lineIndex}-${matchIndex}`}>
                    {line.substring(lastIndex, match.index)}
                  </span>
                );
              }
              
              const isCurrentMatch = searchResults[currentSearchIndex] === absoluteMatchIndex;
              parts.push(
                <span 
                  key={`highlight-${lineIndex}-${matchIndex}`} 
                  className={isCurrentMatch ? 
                    "bg-yellow-400 text-black" : 
                    "bg-yellow-200 text-black"}
                  id={isCurrentMatch ? "current-search-result" : undefined}
                >
                  {line.substring(match.index, match.index + match.length)}
                </span>
              );
              
              lastIndex = match.index + match.length;
            });
            
            if (lastIndex < line.length) {
              parts.push(
                <span key={`after-${lineIndex}`}>
                  {line.substring(lastIndex)}
                </span>
              );
            }
            
            return (
              <div key={`line-${lineIndex}`} className="leading-6" data-line-number={lineIndex + 1}>
                {parts.length > 0 ? parts : ' '}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render the appropriate file preview based on type
  const renderFilePreview = () => {
    if (!activeFile) return null;

    const format = activeFile.format.toLowerCase();
    
    // PDF Preview
    if (format === 'pdf') {
      return (
        <div className="h-full flex flex-col">
          {/* PDF Controls */}
          <div className="flex justify-between items-center p-3 bg-gray-100 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                title="Previous page"
              >
                <ChevronLeft size={20} />
              </button>
              
              <div className="flex items-center">
                <span className="text-sm">Page</span>
                <input
                  type="number"
                  min={1}
                  max={numPages}
                  value={pageNumber}
                  onChange={(e) => setPageNumber(Math.max(1, Math.min(numPages, parseInt(e.target.value) || 1)))}
                  className="w-12 mx-2 p-1 bg-white text-gray-900 text-center border rounded"
                />
                <span className="text-sm">of {numPages}</span>
              </div>
              
              <button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                title="Next page"
              >
                <ChevronRight size={20} />
              </button>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={zoomOut}
                className="p-1 rounded hover:bg-gray-200"
                title="Zoom out"
              >
                <ZoomOut size={18} />
              </button>
              
              <span className="text-sm">{Math.round(scale * 100)}%</span>
              
              <button
                onClick={zoomIn}
                className="p-1 rounded hover:bg-gray-200"
                title="Zoom in"
              >
                <ZoomIn size={18} />
              </button>
            </div>
          </div>
          
          {/* PDF Document */}
          <div className="flex-1 overflow-auto flex justify-center p-4 bg-gray-50">
            <Document
              file={activeFile.url}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div className="flex flex-col items-center justify-center p-8">
                  <Loader className="animate-spin h-8 w-8 text-violet-500 mb-4" />
                  <p className="text-gray-600">Loading PDF...</p>
                </div>
              }
              error={
                <div className="text-center p-8">
                  <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-500">File not found</p>
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                className="shadow-lg"
                loading={
                  <div className="h-[600px] w-[400px] bg-white flex items-center justify-center">
                    <Loader className="animate-spin h-6 w-6 text-violet-500" />
                  </div>
                }
              />
            </Document>
          </div>
          
          {/* PDF Keyboard shortcuts info */}
          <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              Use arrow keys to navigate • Ctrl/Cmd + +/- to zoom • Home/End for first/last page
            </div>
          </div>
        </div>
      );
    }
    
    // Image Preview
    if (['jpg', 'jpeg', 'png', 'gif'].includes(format)) {
      return (
        <div className="h-full flex items-center justify-center bg-gray-50 p-4">
          <img 
            src={activeFile.url} 
            alt={activeFile.name} 
            className="max-w-full max-h-full object-contain"
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          />
        </div>
      );
    }
    
    // Excel File Preview - Use Microsoft Office Online Viewer
    if (['xlsx', 'xls'].includes(format)) {
      return (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="p-3 bg-gray-100 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
              </div>
            </div>
          </div>
          
          <div className="flex-1 bg-white">
            <iframe
              src={createExcelViewerUrl()}
              className="w-full h-full border-0"
              title="Excel Viewer"
              allowFullScreen={true}
            />
          </div>
        </div>
      );
    }
    
    // Fallback Excel Table View if iframe doesn't work and we have parsed data
    if (['xlsx', 'xls'].includes(format) && excelData) {
      const headers = excelData.length > 0 ? Object.keys(excelData[0]) : [];
      
      return (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="p-3 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <FileText size={16} className="text-green-600 mr-2" />
              <span className="font-medium text-sm">Excel Preview</span>
            </div>
            <span className="text-xs text-gray-500">{excelData.length} rows</span>
          </div>
          
          <div className="flex-1 overflow-auto p-4">
            {excelData.length === 0 ? (
              <div className="text-center p-4 text-gray-500">
                No data found in this Excel file
              </div>
            ) : (
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {headers.map((header, index) => (
                      <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {excelData.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {headers.map((header, colIndex) => (
                        <td key={colIndex} className="px-4 py-2 text-sm border">
                          {row[header] !== undefined ? String(row[header]) : ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      );
    }
    
    // Text File Preview with search functionality
    if (['txt', 'csv', 'json', 'xml', 'log', 'md'].includes(format) && textContent) {
      return (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="flex items-center p-2 bg-gray-100 border-b border-gray-200">
            <div className="relative flex-grow">
              <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                <Search size={16} className="text-gray-500" />
              </span>
              <input
                type="text"
                value={fileSearchTerm}
                onChange={(e) => setFileSearchTerm(e.target.value)}
                placeholder="Search in text..."
                className="w-full py-1 pl-8 pr-3 bg-white text-gray-900 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div className="flex items-center ml-2">
              <span className="text-sm text-gray-600 mr-2">
                {searchResults.length > 0 ? 
                  `${currentSearchIndex + 1} of ${searchResults.length}` : 
                  "No results"}
              </span>
              <button 
                onClick={handleSearchPrevious}
                disabled={searchResults.length === 0}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous match"
              >
                <MoveUp size={16} className="text-gray-700" />
              </button>
              <button 
                onClick={handleSearchNext}
                disabled={searchResults.length === 0}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next match"
              >
                <MoveDown size={16} className="text-gray-700" />
              </button>
            </div>
          </div>
          
          <div className="flex-grow overflow-auto">
            {renderTextContent()}
          </div>
        </div>
      );
    }
    
    // Word Document Preview (use iframe with Google Docs Viewer)
    if (['docx', 'doc'].includes(format)) {
      const encodedFileUrl = encodeURIComponent(activeFile.url);
      const viewerUrl = `https://docs.google.com/gview?url=${encodedFileUrl}&embedded=true`;
      
      return (
        <div className="h-full flex flex-col overflow-hidden">
          <div className="p-3 bg-gray-100 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
              </div>
            </div>
          </div>
          
          <div className="flex-1 bg-white">
            <iframe
              src={viewerUrl}
              className="w-full h-full border-0"
              title={`Preview of ${activeFile.name}`}
            />
          </div>
        </div>
      );
    }
    
    // Generic fallback for unsupported file types
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-50 p-8">
        <FileText className="h-16 w-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          Preview not available
        </h3>
        <p className="text-gray-500 mb-6 text-center max-w-md">
          This file type ({activeFile.format}) cannot be previewed directly in the browser.
        </p>
      </div>
    );
  };

  return (
    <>
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => { if (!showDuplicateModal && !showSecurityReport && !showScanConfirmation && !showBulkSecurityReport && !isScanningFiles) onClose(); }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 scrim" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative bg-surface-overlay text-primary rounded-2xl px-6 py-5 text-left align-middle shadow-xl ring-1 ring-black/5 dark:ring-gray-700 transition-all w-full max-w-6xl h-[650px] overflow-hidden">
                
                {/* Header */}
                <div className="flex justify-between items-center pb-4 mb-2 border-b">
                  <div className="flex items-center">
                  {view === 'preview' && (
                    <button
                      onClick={handleBackToList}
                      className="mr-3 p-2 rounded-full hover:bg-gray-200"
                      title="Close preview"
                    >
                   <X size={20} />
                  </button>
                   )}
                    <div>
                      {!userDetails ? (
                        // Loading state with shimmer effect
                        <div className="animate-pulse">
                          <div className="h-7 bg-gray-300 rounded w-40 mb-2"></div>
                          {view === 'list' && (
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                          )}
                        </div>
                      ) : (
                        // Loaded state
                        <>
                          <h2 className="text-xl font-semibold">
                            {view === 'preview' && activeFile 
                              ? `Preview: ${activeFile.name}` 
                              : (isAdmin ? 'File Management' : 'My Files')
                            }
                          </h2>
                          {view === 'list' && (
                            <p className="text-sm text-gray-500 mt-1">
                             {fileData?.search ? (
                              <>
                               {fileData.search.total_matches} result{fileData.search.total_matches !== 1 ? 's' : ''} for "{fileData.search.keyword}"
                               <span className="text-xs text-gray-400 ml-2">
                                 (searched in: file name, brainbank name, session name)
                               </span>
                             </>
                           ) : (
                             `${filteredFiles.length} file${filteredFiles.length !== 1 ? 's' : ''} found`
                           )}
                         </p>
                         )}
                        </>
                      )}
                    </div>
                  </div>
                  
                 <div className="flex items-center space-x-2">
                   {view === 'preview' && activeFile && (
                     <button
                       onClick={handleDownload}
                      className="p-2 rounded hover:bg-gray-200"
                      title="Open in new tab"
                      >
                       <ExternalLink size={18} />
                     </button>
                    )}
                   {view === 'list' && (
                     <>
                       <UploadWorkflowButton onClick={() => setShowUploadSourceModal(true)} />
                       <input 
                         type="file" 
                         multiple 
                         ref={fileInputRef} 
                         onChange={handleFileUpload} 
                         className="hidden" 
                         accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                       />
                       <button
                         onClick={onClose}
                         className="rounded-full p-2 hover:bg-gray-200"
                        >
                         <X size={20} />
                       </button>
                     </>
                  )}
               </div>
                </div>

                {/* Content */}
                {view === 'list' ? (
                  // Files List View
                  <>
                    {/* Compliance Dashboard */}
                    {fileData?.stats && (
                      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <p className="text-sm text-gray-500 mb-1">Total Files</p>
                          <p className="text-2xl font-bold text-gray-800">{fileData.pagination.total_items}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <p className="text-sm text-gray-500 mb-1">Safe Files</p>
                          <p className="text-2xl font-bold text-green-600">{fileData.stats.safe_files_count || 0}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <p className="text-sm text-gray-500 mb-1">Flagged Files</p>
                          <p className="text-2xl font-bold text-orange-600">{fileData.stats.flagged_files_count || 0}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <p className="text-sm text-gray-500 mb-1">Total Findings</p>
                          <div className="flex space-x-2 text-xs mt-1">
                            <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">Cards: {fileData.stats.total_credit_cards || 0}</span>
                            <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded">Emails: {fileData.stats.total_emails || 0}</span>
                            <span className="bg-pink-50 text-pink-700 px-2 py-1 rounded">Phones: {fileData.stats.total_phones || 0}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Search Bar & Filters */}
                    <div className="mb-6">
                     <div className="flex flex-wrap gap-2">
                      <div className="relative flex-1 min-w-[200px]">
                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search files, brainbanks, uploaders..."
                          value={pendingSearch}
                          onChange={handleSearchInputChange}
                          onKeyPress={handleSearchKeyPress}
                          className="w-full pl-10 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-violet-400"
                        />
                     </div>
                     
                     <select
                       value={scanStatusFilter}
                       onChange={(e) => setScanStatusFilter(e.target.value)}
                       className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                     >
                       <option value="">All Scan Status</option>
                       <option value="clean">Clean</option>
                       <option value="flagged">Flagged</option>
                       <option value="blocked">Blocked</option>
                       <option value="failed">Failed</option>
                       <option value="pending">Pending</option>
                       <option value="not_scanned">Not Scanned</option>
                     </select>

                     <select
                       value={findingTypeFilter}
                       onChange={(e) => setFindingTypeFilter(e.target.value)}
                       className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                     >
                       <option value="">All Finding Types</option>
                       <option value="Credit Card">Credit Card</option>
                       <option value="Email Address">Email Address</option>
                       <option value="Phone Number">Phone Number</option>
                     </select>
  
                     <button
                       onClick={handleSearchClick}
                       disabled={isSearching || loading}
                       className="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[80px] justify-center"
                      >
                        {isSearching || loading ? (
                          <Loader size={16} className="animate-spin" />
                     ) : (
                       <>
                         <Search size={16} />
                        Search
                       </>
                      )}
                    </button>
  
                    {(searchTerm || pendingSearch) && (
                     <button
                       onClick={handleClearSearch}
                       disabled={isSearching || loading}
                       className="px-3 py-2 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                       title="Clear search"
                     >
                      <X size={16} />
                    </button>
                   )}
                 </div>
                    </div>
                    {/* Simple Pagination - Previous/Next Only */}
                    {!loading && fileData && totalPages > 1 && (
                      <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
                       <button
                         onClick={() => handlePageChange(currentPage - 1)}
                         disabled={currentPage <= 1}
                         className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                       >
                        <ChevronLeft size={16} className="mr-1" />
                        Previous
                      </button>
    
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                     </span>
    
                     <button
                       onClick={() => handlePageChange(currentPage + 1)}
                       disabled={currentPage >= totalPages}
                       className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                     >
                      Next
                    <ChevronRight size={16} className="ml-1" />
                  </button>
                 </div>
                )}
                {/* Files Table */}
                   <div className="overflow-y-auto h-[calc(100%-190px)]">
                    {(loading || isSearching) ? (
                      <TableSkeleton />
                    ) : filteredFiles.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64">
                          <AlertCircle size={40} className="text-gray-400 mb-2" />
                          <div className="text-center">
                           <p className="text-gray-500">
                             {fileData?.search 
                               ? `No files match "${fileData.search.keyword}"` 
                               : isAdmin ? 'No files found' : 'No files you have access to'}
                            </p>
                            {fileData?.search && (
                              <p className="text-xs text-gray-400 mt-2">
                                Try different keywords or check spelling
                              </p>
                            )}
                        </div>
                        </div>
                      ) : (
                        <table className="min-w-full">
                          <thead className="bg-white border-b">
                            <tr>
                              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                NAME
                              </th>
                              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                TYPE
                              </th>
                              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                FORMAT
                              </th>
                              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                BRAINBANK NAME
                              </th>
                              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                DEPARTMENT
                              </th>
                              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                PROCESSING MODE
                              </th>
                              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                UPLOADED
                              </th>
                              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                UPLOADER
                              </th>
                              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                FINDINGS
                              </th>
                              <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                ACTIONS
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {uploadingFiles.map((file) => (
                              <tr key={file.id} className="bg-gray-50 opacity-80">
                                <td className="py-3 px-4">
                                  <div className="flex items-center">
                                    {getFileIcon(file.format)}
                                    <div className="ml-2">
                                      <div className="text-sm text-gray-900 truncate max-w-[200px]" title={file.name}>
                                        {file.name}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-violet-100 text-violet-800 border border-violet-200">
                                    Direct Upload
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800">
                                    {file.format}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-xs text-gray-400 italic">-</span>
                                </td>
                                <td className="py-3 px-4">-</td>
                                <td className="py-3 px-4">-</td>
                                <td className="py-3 px-4 text-xs text-gray-500">Just now</td>
                                <td className="py-3 px-4 text-sm text-gray-500">You</td>
                                <td className="py-3 px-4 text-sm text-gray-500">-</td>
                                <td className="py-3 px-4 text-center">
                                  {file.status === 'checking' ? (
                                    <span className="text-xs text-blue-500 font-medium animate-pulse">Checking...</span>
                                  ) : file.status === 'error' ? (
                                    <span 
                                      className="text-xs text-red-500 font-medium cursor-help" 
                                      title={file.error}
                                    >
                                      {file.error?.startsWith('Blocked:') ? 'Blocked' : 'Failed'}
                                    </span>
                                  ) : file.status === 'success' ? (
                                    <span className="text-xs text-green-500 font-medium">Done</span>
                                  ) : (
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                      <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${file.progress}%` }}></div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                            {filteredFiles.map((file, index) => (
                              <tr key={`${file.file_id}-${file.storage_location}-${index}`} className="hover:bg-gray-50">
                                <td className="py-3 px-4">
                                  <div className="flex items-center">
                                    {getFileIcon(file.file_format)}
                                    <div className="ml-2 flex items-center gap-2 min-w-0">
                                  <div
                                    className="text-sm text-gray-900 truncate max-w-[200px]"
                                    title={file.original_file_name || file.file_name}
                                  >
                                   {file.original_file_name || file.file_name}
                                  </div>
                                  {file.locked_by && (
                                    <LockBadge
                                      className="flex-shrink-0"
                                      label={
                                        file.locked_at
                                          ? `Locked on ${new Date(file.locked_at).toLocaleDateString()}`
                                          : "Locked"
                                      }
                                    />
                                  )}
                                </div>
                              </div>
                             </td>
                                <td className="py-3 px-4">
                                  {getFileTypeBadge(
                                    file.storage_location,
                                    file.status
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800">
                                    {file.file_format}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  {file.knowledge_base_name ? (
                                    <div className="flex items-center">
                                      <Database size={12} className="mr-1 text-green-600" />
                                      <span className="text-sm text-gray-900 truncate max-w-[150px]" title={file.knowledge_base_name}>
                                        {file.knowledge_base_name}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400 italic">-</span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  {file.department && file.department.trim() !== "" ? (
                                    <div className="flex items-center">
                                      <FolderOpen size={12} className="mr-1 text-blue-600" />
                                      <span className="text-sm text-gray-700 truncate max-w-[120px]" title={file.department}>
                                        {file.department}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400 italic">-</span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  {file.storage_location === 'brainbank' ? (
                                    file.processing_mode === 'advanced' ? (
                                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                        <Zap size={10} className="mr-1" />
                                        Advanced
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                                        <Clock size={10} className="mr-1" />
                                        Standard
                                      </span>
                                    )
                                  ) : (
                                    <span className="text-xs text-gray-400 italic">-</span>
                                  )}
                                </td>
                                <td className="py-3 px-4 whitespace-nowrap text-xs text-gray-500">
                                  <div className="flex items-center">
                                    <Calendar size={12} className="mr-1" />
                                    {formatDate(file.uploaded_at)}
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-sm text-gray-500">
                                  <div className="flex items-center">
                                    <User size={12} className="mr-1 text-gray-400" />
                                    <span className={`text-xs truncate max-w-24 ${file.uploaded_by_name === 'You' ? 'text-violet-600 font-medium' : ''}`} title={file.uploaded_by_name}>
                                      {file.uploaded_by_name}
                                    </span>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  {(file as any).scan_findings && (file as any).scan_findings.length > 0 ? (
                                    <div className="flex flex-wrap gap-1 max-w-[150px]">
                                      {(file as any).scan_findings.map((finding: any, i: number) => (
                                        <span key={i} className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-600 border border-gray-200" title={`${finding.type}: ${finding.count}`}>
                                          {finding.type} ({finding.count})
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-gray-400 italic">None</span>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-center">
                                 <div className="flex items-center justify-center space-x-2">
                                  <button
                                    onClick={() => {
                                      if (onChatWithFile) {
                                        onChatWithFile(file);
                                      }
                                   }}
                                   disabled={isFileLoading === file.file_id}
                                   className="inline-flex items-center justify-center p-1.5 rounded-md hover:bg-green-50 text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                   title="Chat with this file"
                                  >
                                    {isFileLoading === file.file_id ? (
                                     <Loader className="animate-spin" size={18} />
                                   ) : (
                                    <MessageSquare size={18} />
                                   )}
                                 </button>
                                 <button
                                   onClick={() => handlePreviewClick(file)}
                                   disabled={loadingFileId === file.file_id}
                                   className="inline-flex items-center justify-center p-1.5 rounded-md hover:bg-violet-50 text-violet-600 hover:text-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                   title="Preview file"
                                  >
                                    {loadingFileId === file.file_id ? (
                                      <Loader className="animate-spin" size={18} />
                                    ) : (
                                     <Eye size={18} />
                                    )}
                                   </button>
                                   <button
                                     onClick={() => openDeleteConfirmation(file)}
                                     disabled={isDeletingFileId === file.file_id}
                                     className="inline-flex items-center justify-center p-1.5 rounded-md hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                     title="Delete file"
                                    >
                                     {isDeletingFileId === file.file_id ? (
                                      <Loader className="animate-spin" size={18} />
                                   ) : (
                                    <Trash2 size={18} />
                                   )}
                                 </button>
                                 {/* Lock — admin only; renders nothing for regular users */}
                                 <LockButton
                                   resourceType="file"
                                   resourceId={file.file_id}
                                   resource={file}
                                   resourceName={file.original_file_name || file.file_name}
                                   ownerId={file.uploaded_by_id}
                                   fileType={lockFileType(file)}
                                   onChange={() => fetchFiles(currentPage, pageSize)}
                                   className="inline-flex items-center justify-center p-1.5 rounded-md hover:bg-amber-50 transition-colors"
                                 />
                                  {/* Security Report Icon */}
                                  <button
                                    onClick={() => {
                                      setSecurityReportFile(file);
                                      setShowSecurityReport(true);
                                    }}
                                    className="inline-flex items-center justify-center p-1.5 rounded-md transition-colors"
                                    title={`Security: ${file.scan_status === 'not_scanned' ? 'Not Scanned' : (file.scan_status || 'clean')}`}
                                    style={{
                                      color: file.scan_status === 'blocked' ? '#ef4444'
                                           : file.scan_status === 'flagged' ? '#f59e0b'
                                           : file.scan_status === 'not_scanned' ? '#6b7280'
                                           : '#22c55e',
                                    }}
                                    onMouseOver={(e) => {
                                      e.currentTarget.style.backgroundColor =
                                        file.scan_status === 'blocked' ? '#fef2f2'
                                        : file.scan_status === 'flagged' ? '#fffbeb'
                                        : file.scan_status === 'not_scanned' ? '#f3f4f6'
                                        : '#f0fdf4';
                                    }}
                                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                  >
                                    {file.scan_status === 'blocked' ? <ShieldAlert size={18} />
                                     : file.scan_status === 'flagged' ? <Shield size={18} />
                                     : file.scan_status === 'not_scanned' ? <Shield size={18} />
                                     : <ShieldCheck size={18} />}
                                  </button>
                                   {file.storage_location !== 'session' && (
                                     <button
                                       onClick={async () => {
                                         try {
                                           const { rescanFile } = await import('../../service/apiService');
                                           await rescanFile(file.file_id);
                                           toast.success(`Rescan queued for ${file.file_name}`);
                                           await fetchFiles(currentPage, pageSize);
                                         } catch (err: any) {
                                           toast.error(`Failed to rescan ${file.file_name}`);
                                         }
                                       }}
                                       className="inline-flex items-center justify-center p-1.5 rounded-md hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                                       title="Rescan file"
                                     >
                                       <Zap size={18} />
                                     </button>
                                   )}
                               </div>
                              </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                ) : (
                  // File Preview View
                  <div className="h-[calc(100%-70px)]">
                    {fileError ? (
                      <div className="flex flex-col items-center justify-center h-full">
                        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                        <h3 className="text-lg font-semibold mb-2">Preview Failed</h3>
                        <p className="text-gray-600 mb-6">{fileError}</p>
                        <button
                          onClick={handleBackToList}
                          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                        >
                          Back to Files
                        </button>
                      </div>
                    ) : (
                      renderFilePreview()
                    )}
                  </div>
                )}
                {/* Delete Confirmation Modal */}
                {showDeleteConfirmation && fileToDelete && (
                  <div className="fixed inset-0 z-[100] bg-black bg-opacity-40 flex items-center justify-center">
                    <div className="bg-surface-overlay text-primary rounded-lg shadow-xl ring-1 ring-black/5 dark:ring-gray-700 max-w-md w-full p-6 mx-4">
                      <h3 className="text-lg font-medium mb-4 text-primary">Delete File</h3>
                      <p className="mb-6 text-gray-600">
                        Are you sure you want to delete "<span className="font-semibold">{fileToDelete.name}</span>"? This action cannot be undone.
                      </p>
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => {
                            setShowDeleteConfirmation(false);
                            setFileToDelete(null);
                          }}
                          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                          disabled={isDeletingFileId !== null}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteFile}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center"
                          disabled={isDeletingFileId !== null}
                        >
                          {isDeletingFileId === fileToDelete.id ? (
                            <>
                              <Loader size={16} className="animate-spin mr-2" />
                              Deleting...
                            </>
                          ) : (
                            'Delete'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              <UploadSourceModal 
        isOpen={showUploadSourceModal} 
        onClose={() => setShowUploadSourceModal(false)}
        onLocalClick={handleUploadClick}
        onGoogleDriveClick={handleGoogleDriveUploadClick}
        onDropboxClick={handleDropboxUploadClick}
        isDropboxConfigured={isDropboxConfigured}
      />
      
      <DropboxFilePicker
        isOpen={isDropboxPickerOpen}
        onClose={() => setIsDropboxPickerOpen(false)}
        onFileSelect={handleDropboxFileSelect}
      />
      <DropboxAuth
        isOpen={isDropboxAuthOpen}
        onClose={() => setIsDropboxAuthOpen(false)}
        onAuthSuccess={handleDropboxAuthSuccess}
      />

      <GoogleDriveFilePicker
        isOpen={isGoogleDrivePickerOpen}
        onClose={() => setIsGoogleDrivePickerOpen(false)}
        onFileSelect={handleGoogleDriveFileSelect}
      />
      <GoogleDriveAuth
        isOpen={isGoogleDriveAuthOpen}
        onClose={() => setIsGoogleDriveAuthOpen(false)}
        onAuthSuccess={handleGoogleDriveAuthSuccess}
      />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* Duplicate File Detection Modal */}
    {showDuplicateModal && duplicateFiles.length > 0 && (
      <div 
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" 
        onMouseDown={e => e.stopPropagation()} 
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 bg-amber-50 border-b border-amber-200">
            <AlertTriangle size={22} className="text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Duplicate {duplicateFiles.length === 1 ? 'File' : 'Files'} Detected
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {duplicateFiles.length === 1
                  ? 'This file already exists in the system'
                  : `${duplicateFiles.length} files already exist in the system`}
              </p>
            </div>
          </div>

          {/* File List */}
          <div className="px-6 py-4 max-h-[300px] overflow-y-auto space-y-3">
            {duplicateFiles.map((dup, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1 mr-3">
                    <p className="text-sm font-medium text-gray-900 truncate" title={dup.name}>
                      {dup.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Matches existing: <span className="font-medium text-gray-700">{dup.existingFileName}</span>
                    </p>
                  </div>
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800 flex-shrink-0">
                    {dup.format}
                  </span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => handleSkipDuplicate(index)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Skip
                  </button>
                  <button
                    onClick={() => handleUploadAnyway(index)}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors"
                  >
                    Upload Anyway
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          {duplicateFiles.length > 1 && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <button
                onClick={handleSkipAllDuplicates}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Skip All Duplicates
              </button>
            </div>
          )}
        </div>
      </div>
    )}
    {/* Scan Confirmation Modal */}
    
      <ScanConfirmationModal
      isOpen={showScanConfirmation}
      fileCount={pendingScanFiles.length}
      fileNames={pendingScanFiles.map(f => f.name)}
      onScanAndUpload={() => {
        setShowScanConfirmation(false);
        const files = [...pendingScanFiles];
        setPendingScanFiles([]);
        executeUpload(files, true);
      }}
      onUploadWithoutScan={() => {
        setShowScanConfirmation(false);
        const files = [...pendingScanFiles];
        setPendingScanFiles([]);
        executeUpload(files, false);
      }}
      onCancel={() => {
        setShowScanConfirmation(false);
        setPendingScanFiles([]);
      }}
    />

    
    {/* Scanning Loading Modal */}
    {isScanningFiles && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
        <SecurityScannerAnimation fileName={uploadingFiles.length === 1 ? uploadingFiles[0].name : `${uploadingFiles.length} files`} />
      </div>
    )}

    {/* Bulk Security Report Modal */}
    <BulkSecurityReportModal
      isOpen={showBulkSecurityReport}
      results={bulkScanResults}
      onClose={() => {
        setShowBulkSecurityReport(false);
        setPendingScanFiles([]);
        setBulkScanResults([]);
      }}
      onUpload={() => {
        setShowBulkSecurityReport(false);
        const files = [...pendingScanFiles];
        const results = [...bulkScanResults];
        setPendingScanFiles([]);
        setBulkScanResults([]);
        executeUpload(files, true, results);
      }}
    />

    {/* Security Report Modal */}
    <SecurityReportModal
      isOpen={showSecurityReport}
      onClose={() => {
        setShowSecurityReport(false);
        setSecurityReportFile(null);
      }}
      fileName={securityReportFile?.file_name || ""}
      scanStatus={securityReportFile?.scan_status || "clean"}
      scanIssues={securityReportFile?.scan_issues || []}
      scanFindings={(securityReportFile as any)?.scan_findings || []}
      lastScanDate={(securityReportFile as any)?.last_scan_date || null}
      scanCount={(securityReportFile as any)?.scan_count || 0}
    />

    </>
  );
};

export default FileManagementModal;